import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdKeyboardArrowDown, MdSearch } from 'react-icons/md';

// Liste déroulante avec recherche (sans tenir compte des accents ni de la casse), options
// éventuellement groupées. Une recherche qui correspond au nom d'un groupe garde tout le groupe.
// La liste est en position fixe : un conteneur overflow (tableau) ne la coupe pas.
export interface SearchSelectOption {
    value: string;
    label: string;
    group?: string;
    // Texte secondaire affiché à droite du libellé sélectionné (ex. l'industrie d'un produit).
    hint?: string;
}

const normalize = (value: string) =>
    value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const LIST_WIDTH = 320;

export default function SearchSelect({
    options,
    value,
    onChange,
    placeholder = 'Choisir…',
    searchPlaceholder = 'Rechercher…',
    clearable,
    disabled,
    className = 'w-64',
}: {
    options: SearchSelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    // Affiche une croix pour revenir à '' (ex. filtre « Tous »).
    clearable?: boolean;
    disabled?: boolean;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open) return;
        setQuery('');
        setTimeout(() => inputRef.current?.focus(), 0);
        const close = (e: Event) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        // La liste ne suit pas le défilement de la page : on la ferme.
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', close, true);
            window.removeEventListener('resize', close);
        };
    }, [open]);

    const filtered = useMemo(() => {
        const q = normalize(query);
        if (!q) return options;
        return options.filter((o) => normalize(o.label).includes(q) || (o.group && normalize(o.group).includes(q)));
    }, [options, query]);

    const groups = useMemo(() => {
        const result: { group?: string; options: SearchSelectOption[] }[] = [];
        filtered.forEach((option) => {
            const last = result[result.length - 1];
            if (last && last.group === option.group) last.options.push(option);
            else result.push({ group: option.group, options: [option] });
        });
        return result;
    }, [filtered]);

    const selected = options.find((o) => o.value === value);

    const toggle = () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) setPosition({ left: Math.max(8, Math.min(rect.left, window.innerWidth - LIST_WIDTH - 8)), top: rect.bottom + 4 });
        setOpen(!open);
    };

    const pick = (next: string) => {
        onChange(next);
        setOpen(false);
    };

    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                ref={buttonRef}
                type="button"
                onClick={toggle}
                disabled={disabled}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-left text-sm focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            >
                {selected ? (
                    <span className="truncate">
                        {selected.label}
                        {selected.hint && <span className="text-xs text-gray-400"> · {selected.hint}</span>}
                    </span>
                ) : (
                    <span className="truncate text-gray-400">{placeholder}</span>
                )}
                <span className="flex shrink-0 items-center gap-0.5">
                    {clearable && value && (
                        <span
                            role="button"
                            aria-label="Effacer"
                            onClick={(e) => {
                                e.stopPropagation();
                                pick('');
                            }}
                            className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-navy-600"
                        >
                            <MdClose size={14} />
                        </span>
                    )}
                    <MdKeyboardArrowDown className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                </span>
            </button>
            {open && position && (
                <div
                    style={{ position: 'fixed', left: position.left, top: position.top, width: LIST_WIDTH }}
                    className="z-[1000] rounded-xl border border-gray-100 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800"
                >
                    <div className="relative border-b border-gray-100 p-2 dark:border-navy-600">
                        <MdSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') setOpen(false);
                                // Entrée : premier résultat.
                                if (e.key === 'Enter' && filtered.length) pick(filtered[0].value);
                            }}
                            placeholder={searchPlaceholder}
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-8 pr-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto p-1">
                        {filtered.length === 0 ? (
                            <p className="px-3 py-4 text-center text-sm text-gray-500">Aucun résultat.</p>
                        ) : (
                            groups.map(({ group, options: groupOptions }, index) => (
                                <div key={`${group ?? ''}-${index}`}>
                                    {group && (
                                        <div className="px-2 pb-0.5 pt-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">{group}</div>
                                    )}
                                    {groupOptions.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            onClick={() => pick(option.value)}
                                            className={`block w-full rounded-md px-3 py-1.5 text-left text-sm text-navy-700 hover:bg-gray-100 dark:text-white dark:hover:bg-navy-700 ${
                                                option.value === value ? 'bg-brand-50 font-medium dark:bg-navy-700' : ''
                                            }`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
